import { Row } from "@tanstack/react-table";

import { getBadgeVariant } from "../sub-product-items/table/ItemTable";

import AvatarText from "@/components/reusables/AvatarText";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { SELECTOR_ID } from "@/constants/global-constants";
import { ExtendedColumnDef } from "@/interfaces/globals";
import { SubProductsItem } from "@/interfaces/tools.interface";
import { cn, formatToCapitalizedWords } from "@/lib/utils";

type TUseProductDetailTableColumns = {
  writePermissions?: boolean;
  getActionOptions: (row: Row<SubProductsItem>) => ActionButtonDataTypes[];
};

export const useProductDetailTableColumns = ({
  writePermissions,
  getActionOptions,
}: TUseProductDetailTableColumns): ExtendedColumnDef<SubProductsItem>[] => {
  return [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: SELECTOR_ID,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
          className={cn(
            "hidden group-hover/header:block",
            table.getIsSomePageRowsSelected() && "block",
            table.getIsAllPageRowsSelected() && "!block",
          )}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
          onClick={(e) => e.stopPropagation()}
        />
      ),

      size: 12,
      enableSorting: false,
      enableHiding: false,
    },
    {
      map: {
        key: "Name",
        dataTransformer: (data) => data?.name || "-",
      },
      accessorKey: "Name",
      header: "Name",
      cell: ({ row }): JSX.Element => {
        return (
          <AvatarText
            src={row?.original?.images?.[0]}
            name={`${row.original.name}`}
            title={`${row.original.name}`}
            classNames={{
              titleClassName: "line-clamp-1 text-wrap",
            }}
          />
        );
      },
      meta: {
        showRightBorder: true,
        classNameRow: "max-w-[100px]",
      },
    },
    {
      map: {
        key: "Model Number",
        dataTransformer: (data) => data?.modelNumber || "-",
      },
      accessorKey: "Model Number",
      header: "Model Number",
      cell: ({ row }) => row.original?.modelNumber ?? "-",
      meta: {
        showRightBorder: true,
        className: "max-w-[60px]",
        classNameRow: "max-w-[60px]",
      },
    },
    {
      map: {
        key: "Status",
        dataTransformer: (data) =>
          formatToCapitalizedWords(data?.status || "-"),
      },
      accessorKey: "Status",
      header: "Status",
      cell: ({ row }): JSX.Element => {
        return (
          <Badge variant={getBadgeVariant(row.original?.status)}>
            {formatToCapitalizedWords(row.original?.status)}
          </Badge>
        );
      },
      meta: {
        showRightBorder: true,
        className: "max-w-[50px]",
        classNameRow: "max-w-[50px]",
      },
    },
    {
      map: {
        key: "Site Assigned",
        dataTransformer: (data) => data?.project?.address?.label || "-",
      },
      accessorKey: "Site Assigned",
      header: "Site Assigned",
      cell: ({ row }) => row.original?.project?.address?.label || "-",
    },
    {
      id: "actions",
      hideColumn: !writePermissions,
      cell: ({ row }): React.JSX.Element => (
        <CustomDropdown data={getActionOptions(row)} />
      ),
      meta: {
        classNameRow: "w-20",
      },
    },
  ];
};
